import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { AppDialog } from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { TableProvider } from '#/modules/commonComponents/table/TableContext';
import Table from '#/modules/commonComponents/table/Table';
import useTable from '#/hooks/table/useTable';
import MaterialStatusBadge from '#/modules/momMgmt/analysis/orders/components/MaterialStatusBadge';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';

function dash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function isMaterialSufficient(check) {
  const shortage = Number(check?.shortageQty);
  if (Number.isFinite(shortage)) {
    return shortage <= 0;
  }
  return check?.materialStatus === 'READY';
}

function mapMaterialCheckRows(checks = []) {
  return (checks ?? []).map((check, index) => {
    const mtr = check?.mtr ?? check?.material ?? null;
    return {
      id: check?.id != null && check.id !== ''
        ? String(check.id)
        : `material-check-${index}`,
      mtrNo: mtr?.mtrNo ?? '—',
      mtrName: mtr?.name ?? '—',
      sufficient: isMaterialSufficient(check),
      materialStatus: check?.materialStatus,
      requiredQty: check?.requiredQty,
      issuedQty: check?.issuedQty,
      remainingRequiredQty: check?.remainingRequiredQty,
      onHandAllocatedQty: check?.onHandAllocatedQty,
      inTransitAllocatedQty: check?.inTransitAllocatedQty,
      lateInTransitQty: check?.lateInTransitQty,
      shortageQty: check?.shortageQty,
      earliestCoverDate: check?.earliestCoverDate,
    };
  });
}

function buildMaterialCheckColumns() {
  return [
    {
      id: 'mtrNo',
      header: '料號',
      accessorKey: 'mtrNo',
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        noTruncate: true,
        filterVariant: 'text',
        filterMode: 'client',
        filterPlaceholder: '搜尋料號',
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original.mtrNo} />
      ),
    },
    {
      id: 'mtrName',
      header: '名稱',
      accessorKey: 'mtrName',
      size: 180,
      meta: { cellClassName: 'td_left', noTruncate: true },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original.mtrName} />
      ),
    },
    {
      id: 'sufficient',
      header: '齊全',
      accessorFn: (row) => (row.sufficient ? '是' : '否'),
      size: 80,
      enableColumnFilter: true,
      meta: {
        filterVariant: 'selectSingle',
        filterMode: 'client',
      },
      cell: ({ row }) => (
        <span className={row.original.sufficient ? 'text-emerald-700' : 'text-rose-700'}>
          {row.original.sufficient ? '是' : '否'}
        </span>
      ),
    },
    {
      id: 'materialStatus',
      header: '物料狀態',
      accessorKey: 'materialStatus',
      size: 110,
      enableColumnFilter: true,
      meta: {
        filterVariant: 'selectMulti',
        filterMode: 'client',
      },
      cell: ({ row }) => (
        <MaterialStatusBadge status={row.original.materialStatus} size="sm" />
      ),
    },
    {
      id: 'requiredQty',
      header: '需求',
      accessorFn: (row) => formatDisplayQty(row.requiredQty),
      size: 90,
    },
    {
      id: 'issuedQty',
      header: '已出庫',
      accessorFn: (row) => formatDisplayQty(row.issuedQty),
      size: 90,
    },
    {
      id: 'remainingRequiredQty',
      header: '剩餘需求',
      accessorFn: (row) => formatDisplayQty(row.remainingRequiredQty),
      size: 100,
    },
    {
      id: 'onHandAllocatedQty',
      header: '庫存配置',
      accessorFn: (row) => formatDisplayQty(row.onHandAllocatedQty),
      size: 100,
    },
    {
      id: 'inTransitAllocatedQty',
      header: '在途配置',
      accessorFn: (row) => formatDisplayQty(row.inTransitAllocatedQty),
      size: 100,
    },
    {
      id: 'lateInTransitQty',
      header: '逾期在途',
      accessorFn: (row) => formatDisplayQty(row.lateInTransitQty),
      size: 100,
    },
    {
      id: 'shortageQty',
      header: '缺口',
      accessorFn: (row) => formatDisplayQty(row.shortageQty),
      size: 90,
    },
    {
      id: 'earliestCoverDate',
      header: '最早覆蓋日',
      accessorFn: (row) => formatDateOrDash(row.earliestCoverDate),
      size: 120,
    },
  ];
}

const MaterialChecksDialog = ({
  open,
  onOpenChange,
  workOrderNo,
  materialStatus,
  materialChecks = [],
}) => {
  const rows = useMemo(
    () => mapMaterialCheckRows(materialChecks),
    [materialChecks],
  );

  const columns = useMemo(() => buildMaterialCheckColumns(), []);
  const tableState = useTable(
    rows,
    columns,
    { current_page: 1, page_size: 10, total_count: 0, total_page: 1 },
    {
      serverSide: false,
      disableGlobalFilter: true,
      enableRowSelection: false,
      initialPageSize: 200,
    },
  );

  const description = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="truncate text-base font-medium">
        {dash(workOrderNo)}
      </span>
      {materialStatus ? (
        <MaterialStatusBadge status={materialStatus} size="sm" />
      ) : null}
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="物料檢核"
      description={description}
      headerVariant="primary"
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      onCancel={() => onOpenChange(false)}
      cancelText="關閉"
      showCancel
    >
      {rows.length === 0 ? (
        <p className="py-6 text-center text-base text-muted-foreground">
          尚無物料檢核資料
        </p>
      ) : (
        <TableProvider value={tableState}>
          <Table
            loading={false}
            containerClassName="max-h-96 max-w-full overflow-auto"
          />
        </TableProvider>
      )}
    </AppDialog>
  );
};

MaterialChecksDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  workOrderNo: PropTypes.string,
  materialStatus: PropTypes.string,
  materialChecks: PropTypes.arrayOf(PropTypes.object),
};

export default MaterialChecksDialog;

